import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { Seo, breadcrumbSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SectionTag } from "@/components/SectionTag";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTABlock } from "@/components/CTABlock";
import { ARTICLES, CATEGORIES } from "@/data/articles";
import { ARTICLES_EN, CATEGORIES_EN } from "@/data/articles.en";
import { useLang } from "@/i18n/LangContext";

export default function Blog() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(null);
  const { t, l, lang } = useLang();
  const articles = lang === "en" ? ARTICLES_EN : ARTICLES;
  const categories = lang === "en" ? CATEGORIES_EN : CATEGORIES;
  const b = t.blogPage;

  const formatDate = (d) =>
    new Date(d).toLocaleDateString(t.meta.dateLocale, { day: "numeric", month: "long", year: "numeric" });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter((a) => {
      const matchCat = !category || a.category === category || (a.tags || []).includes(category);
      const matchQuery =
        !q ||
        a.title.toLowerCase().includes(q) ||
        a.excerpt.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q);
      return matchCat && matchQuery;
    });
  }, [query, category, articles]);

  return (
    <>
      <Seo
        title={b.seo.title}
        description={b.seo.description}
        path="/blog"
        schemas={[
          breadcrumbSchema([
            { name: t.common.home, path: "/" },
            { name: b.breadcrumb, path: "/blog" },
          ]),
        ]}
      />

      <section className="px-5 md:px-10 pt-36 md:pt-44 pb-14 md:pb-20" data-testid="blog-hero">
        <div className="mx-auto max-w-7xl">
          <Breadcrumb items={[{ label: t.common.home, to: "/" }, { label: b.breadcrumb }]} />
          <div className="max-w-3xl">
            <SectionTag>{b.tag}</SectionTag>
            <Reveal delay={0.1}>
              <h1 className="mt-5 font-display text-4xl md:text-6xl font-extrabold tracking-tighter text-balance">
                {b.h1}
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-7 text-base md:text-lg leading-relaxed text-plum/85">
                {b.subtitle}
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.25} className="mt-12">
            <div className="relative max-w-xl">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-plum/40" aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={b.searchPlaceholder}
                aria-label={b.searchAria}
                data-testid="blog-search-input"
                className="w-full rounded-full border border-ink/10 bg-white py-4 pl-13 pr-6 pl-12 text-sm outline-none transition-colors duration-300 focus:border-terracotta placeholder:text-plum/40"
              />
            </div>
            <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label={b.filterAria}>
              <button
                type="button"
                onClick={() => setCategory(null)}
                data-testid="blog-cat-all"
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-300 ${
                  !category ? "bg-ink text-cream" : "bg-white border border-ink/10 text-plum hover:border-terracotta"
                }`}
              >
                {b.all}
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(category === c ? null : c)}
                  data-testid={`blog-cat-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-300 ${
                    category === c ? "bg-ink text-cream" : "bg-white border border-ink/10 text-plum hover:border-terracotta"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 md:px-10 pb-24 md:pb-32" data-testid="blog-list">
        <div className="mx-auto max-w-7xl">
          {filtered.length === 0 ? (
            <p className="rounded-3xl bg-white p-12 text-center text-sm text-plum/60" data-testid="blog-no-result">
              {b.noResult}
            </p>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((a, i) => (
                <Reveal key={a.slug} delay={(i % 3) * 0.08} className="h-full">
                  <Link
                    to={l(`/blog/${a.frSlug || a.slug}`)}
                    data-testid={`blog-article-${a.slug}`}
                    className="group flex h-full flex-col overflow-hidden rounded-3xl border border-ink/5 bg-white transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_28px_50px_-15px_rgba(17,14,13,0.12)]"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={a.image}
                        alt={a.imageAlt}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-7">
                      <span className="text-xs font-semibold uppercase tracking-[0.14em] text-terracotta-deep">
                        {a.category}
                      </span>
                      <h2 className="mt-3 font-display text-lg font-bold tracking-tight leading-snug">
                        {a.title}
                      </h2>
                      <p className="mt-3 text-sm leading-relaxed text-plum/70 line-clamp-3 flex-1">{a.excerpt}</p>
                      <p className="mt-5 text-xs text-plum/50">
                        {formatDate(a.date)} · {a.readTime} {t.common.readSuffix}
                      </p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTABlock />
    </>
  );
}
